import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getPageMeta, updatePageMeta, type PageMeta } from '@/config/meta';

/**
 * Hook to automatically update page meta based on current route
 * Supports custom meta overrides and dynamic title generation
 * 
 * @param customMeta - Optional partial meta to override base route meta
 *                    Can be a static object or a function that returns meta
 * @param dynamicTitleFn - Optional function to generate dynamic title based on route params
 */
export function usePageMeta(
  customMeta?: Partial<PageMeta> | (() => Partial<PageMeta>),
  dynamicTitleFn?: (params: Record<string, string | undefined>) => string
): void {
  const location = useLocation();
  const params = useParams();

  // Memoize custom meta to avoid unnecessary re-renders
  const memoizedCustomMeta = useMemo(() => {
    if (typeof customMeta === 'function') {
      return customMeta();
    }
    return customMeta;
  }, [customMeta]);

  // Memoize params string to avoid unnecessary effect triggers
  const paramsString = useMemo(() => JSON.stringify(params), [params]);

  // Store dynamicTitleFn in ref to avoid effect re-runs when function reference changes
  const dynamicTitleFnRef = useRef(dynamicTitleFn);
  useEffect(() => {
    dynamicTitleFnRef.current = dynamicTitleFn;
  }, [dynamicTitleFn]);

  useEffect(() => {
    // Get base meta for current path
    const baseMeta = getPageMeta(location.pathname);

    // Merge with custom meta if provided
    let finalMeta: PageMeta = memoizedCustomMeta
      ? { ...baseMeta, ...memoizedCustomMeta }
      : baseMeta;

    // Apply dynamic title function if provided
    if (dynamicTitleFnRef.current && params) {
      const dynamicTitle = dynamicTitleFnRef.current(params);
      if (dynamicTitle) {
        finalMeta = { ...finalMeta, title: dynamicTitle };
      }
    }

    // Update page meta
    updatePageMeta(finalMeta);
  }, [location.pathname, memoizedCustomMeta, paramsString, params]);
}

/**
 * Hook to set completely custom page meta
 * Use this when you need full control over the meta tags
 * 
 * @param meta - Complete PageMeta object
 */
export function useCustomPageMeta(meta: PageMeta): void {
  useEffect(() => {
    updatePageMeta(meta);
  }, [meta]);
}
